import PropTypes from 'prop-types';
import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';

import * as AppPropTypes from '../lib/PropTypes';

import { useApi } from './ApiContext';

const AuthContext = React.createContext(null);

export const useAuth = () => useContext(AuthContext);

export const useUser = () => {
    const { user } = useAuth();
    return user || null;
};

export const useSetUser = () => {
    const { setUser } = useAuth();
    return setUser;
};

const useAuthCheck = () => {
    const [loading, setLoading] = useState(false);
    const api = useApi();
    const check = useCallback(() => {
        setLoading(true);
        return api.account
            .check()
            .then((response) => {
                setLoading(false);
                return response;
            })
            .catch((e) => {
                setLoading(false);
                throw e;
            });
    }, [api, setLoading]);
    return { check, loading };
};

const useAuthLogin = () => {
    const [loading, setLoading] = useState(false);
    const api = useApi();
    const login = useCallback(
        (email, password) => {
            setLoading(true);
            return api.auth
                .login(email, password)
                .then((response) => {
                    setLoading(false);
                    return response;
                })
                .catch((e) => {
                    setLoading(false);
                    throw e;
                });
        },
        [api, setLoading],
    );
    return { login, loading };
};

const useAuthLogout = () => {
    const [loading, setLoading] = useState(false);
    const api = useApi();
    const logout = useCallback(() => {
        setLoading(true);
        return api.auth
            .logout()
            .then((response) => {
                setLoading(false);
                return response;
            })
            .catch((e) => {
                setLoading(false);
                throw e;
            });
    }, [api, setLoading]);
    return { logout, loading };
};

const useAuthRegister = () => {
    const [loading, setLoading] = useState(false);
    const api = useApi();
    const register = useCallback(
        (data) => {
            setLoading(true);
            return api.auth
                .register(data)
                .then((response) => {
                    setLoading(false);
                    return response;
                })
                .catch((e) => {
                    setLoading(false);
                    throw e;
                });
        },
        [api, setLoading],
    );
    return { register, loading };
};

export const useAuthRegisterWithEmail = () => {
    const setUser = useSetUser();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const api = useApi();
    const registerWithEmail = useCallback(
        (data) => {
            setLoading(true);
            setError(false);
            return api.auth
                .registerEmail(data)
                .then((response) => {
                    if (response && response.user) {
                        setUser(response.user);
                    }
                    setLoading(false);
                    return response;
                })
                .catch((e) => {
                    setError(true);
                    setLoading(false);
                    throw e;
                });
        },
        [api, setLoading, setError, setUser],
    );
    return { registerWithEmail, loading, error };
};

export const useAuthRequestPassword = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const api = useApi();
    const requestPassword = useCallback(
        (email) => {
            setError(false);
            setLoading(true);
            return api.auth
                .requestPassword(email)
                .then((response) => {
                    if (response && response.success) {
                        setLoading(false);
                        return response;
                    }
                    throw response;
                })
                .catch((e) => {
                    setError(true);
                    setLoading(false);
                    throw e;
                });
        },
        [api, setLoading, setError],
    );
    return { requestPassword, loading, error };
};

export const useAuthUpdatePassword = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const api = useApi();
    const updatePassword = useCallback(
        (data) => {
            setError(false);
            setLoading(true);
            return api.auth
                .updatePassword(data)
                .then((response) => {
                    setLoading(false);
                    return response;
                })
                .catch((e) => {
                    setError(true);
                    setLoading(false);
                    throw e;
                });
        },
        [api, setLoading, setError],
    );
    return { updatePassword, loading, error };
};

const propTypes = {
    user: AppPropTypes.user,
    checkOnMount: PropTypes.bool,
    children: PropTypes.node.isRequired,
};

const defaultProps = {
    checkOnMount: true,
    user: null,
};

export const AuthProvider = ({ user: initialUser, checkOnMount, children }) => {
    const [user, setUser] = useState(initialUser);
    const { login: authLogin } = useAuthLogin();
    const { logout: authLogout } = useAuthLogout();
    const { check: authCheck } = useAuthCheck();
    const { register: authRegister } = useAuthRegister();

    const login = useCallback(
        (email, password) =>
            authLogin(email, password).then((newUser) => {
                setUser(newUser);
                return newUser;
            }),
        [authLogin, setUser],
    );

    const logout = useCallback(
        () =>
            authLogout().then(() => {
                setUser(null);
            }),
        [authLogout, setUser],
    );

    const register = useCallback(
        (data) =>
            authRegister(data).then((newUser) => {
                setUser(newUser);
                return newUser;
            }),
        [authRegister, setUser],
    );

    useEffect(() => {
        if (checkOnMount && user === null) {
            authCheck()
                .then((newUser = null) => {
                    const { email = null } = newUser || {};
                    if (email !== null) {
                        setUser(newUser);
                    }
                })
                .catch(() => {
                    setUser(null);
                });
        }
    }, [authCheck, setUser, checkOnMount]);

    const value = useMemo(
        () => ({
            user,
            setUser,
            loggedIn: user !== null,
            logout,
            login,
            register,
        }),
        [user, setUser, logout, login, register],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = propTypes;
AuthProvider.defaultProps = defaultProps;

export default AuthContext;
